// ChapterList.tsx
// NOTE: Update frontend/src/__tests__/ChapterList.test.tsx and run
// `../scripts/test-frontend.sh ChapterList` when editing this component.
import React from 'react';
import { Input, Button, Tooltip, Dropdown } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, MenuUnfoldOutlined, MenuFoldOutlined, QuestionCircleOutlined, HolderOutlined, InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Chapter, ChapterReorderPayload } from './types';

interface ChapterListProps {
  chapters: Chapter[];
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  selectedChapter: Chapter | null;
  onSelectChapter: (chapter: Chapter) => void;
  editingChapterGuid: string | null;
  editedChapterName: string;
  onEditChapterName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveChapterName: (chapterGuid: string) => void;
  onCancelEdit: () => void;
  onStartEditingChapter: (chapter: Chapter) => void;
  onDeleteChapter: (chapterGuid: string) => void;
  allowSourceChapterDeletion?: boolean;
  isSelectedChapter: (chapterGuid: string) => boolean;
  onReorderChapters: (payload: ChapterReorderPayload) => void;
  isReordering: boolean;
  disableReorder?: boolean;
  pendingProofreadReviewChapterGuids?: Set<string>;
  pendingProofreadReviewLabel?: string;
}

const ChapterList: React.FC<ChapterListProps> = ({
  chapters,
  isSidebarCollapsed,
  toggleSidebar,
  selectedChapter,
  onSelectChapter,
  editingChapterGuid,
  editedChapterName,
  onEditChapterName,
  onSaveChapterName,
  onCancelEdit,
  onStartEditingChapter,
  onDeleteChapter,
  allowSourceChapterDeletion = true,
  isSelectedChapter,
  onReorderChapters,
  isReordering,
  disableReorder = false,
  pendingProofreadReviewChapterGuids,
  pendingProofreadReviewLabel = 'this version',
}) => {
  const handleDragEnd = (result: DropResult) => {
    if (disableReorder) {
      return;
    }
    const { destination, source, draggableId } = result;
    if (!destination || typeof source?.index !== 'number' || isSidebarCollapsed || isReordering) {
      return;
    }
    if (!draggableId) {
      return;
    }
    if (destination.index === source.index) {
      return;
    }
    onReorderChapters({
      chapterGuid: draggableId,
      sourceIndex: source.index,
      destinationIndex: destination.index,
    });
  };

  const isInteractionLocked = isReordering;

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, chapter: Chapter) => {
    if (isInteractionLocked) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onSelectChapter(chapter);
  };



  return (
    <div
      data-tour-id="chapter-sidebar"
      data-testid="tutorial:book-detail:chapter-rail"
      className={`chapter-sidebar ${isSidebarCollapsed ? 'chapter-sidebar--collapsed' : ''}`}
      style={{
        minWidth: isSidebarCollapsed ? '50px' : '220px',
        maxWidth: isSidebarCollapsed ? '50px' : '220px',
        height: isSidebarCollapsed ? 'auto' : '100%',
      }}
    >
      <div
        className="droppableArea"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: isSidebarCollapsed ? '10px 0' : '12px',
          boxSizing: 'border-box'
        }}
      >
        <div className="chapter-sidebar-header">
          {!isSidebarCollapsed && (
            <div className="chapter-sidebar-title">
                <span>Chapter List</span>
                <Tooltip title="This list contains the chapters of your novel for organizational purposes. Chapter names do not affect the export or translation process.">
                  <span
                    className="chapter-sidebar-help"
                    role="img"
                    aria-label="About the chapter list"
                    tabIndex={0}
                  >
                    <QuestionCircleOutlined />
                  </span>
                </Tooltip>
                <span className="chapter-sidebar-count">
                  {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'}
                </span>
            </div>
          )}
          <Tooltip title={isSidebarCollapsed ? "Expand Chapter List" : "Collapse Chapter List"}>
            <Button 
              type="text" 
              icon={isSidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} 
              onClick={toggleSidebar}
              className="chapter-sidebar-toggle"
              aria-label={isSidebarCollapsed ? "Expand Chapter List" : "Collapse Chapter List"}
            />
          </Tooltip>
        </div>
        {isReordering && !isSidebarCollapsed && (
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginBottom: '8px',
            textAlign: 'left',
            padding: '0 4px'
          }}>
            Saving order…
          </div>
        )}

        
        {!isSidebarCollapsed && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="chapter-list" isDropDisabled={isReordering || disableReorder}>
              {(dropProvided) => (
                <div
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  className="chapter-list-scroll-area"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    padding: '0 8px 12px 0',
                    boxSizing: 'border-box',
                    opacity: isReordering ? 0.6 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  {chapters.map((chapter, index) => (
                    <Draggable
                      key={chapter.chapter_guid}
                      draggableId={chapter.chapter_guid}
                      index={index}
                      isDragDisabled={disableReorder || isReordering || editingChapterGuid === chapter.chapter_guid}
                    >
                      {(dragProvided, snapshot) => (
                        <div 
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          data-testid={index === 0 ? 'tutorial:book-detail:first-chapter' : undefined}
                          className={`chapter-row chapterItem ${isSelectedChapter(chapter.chapter_guid) ? 'chapter-row--active selectedChapter' : ''}`}
                          style={{ 
                            ...(dragProvided.draggableProps.style as React.CSSProperties)
                          }}
                          role="button"
                          tabIndex={isInteractionLocked ? -1 : 0}
                          onClick={() => {
                            if (isInteractionLocked) {
                              return;
                            }
                            onSelectChapter(chapter);
                          }}
                          onKeyDown={(event) => handleRowKeyDown(event, chapter)}
                        >
                          <Tooltip title={disableReorder ? "Chapter reordering is unavailable for EPUB uploads." : "Drag to reorder"}>
                            <div
                              data-tour-id="chapter-reorder-handle"
                              {...dragProvided.dragHandleProps}
                              className={`chapter-reorder-handle ${disableReorder ? 'chapter-reorder-handle--disabled' : ''}`}
                              onClick={(event) => event.stopPropagation()}
                              aria-label={`Reorder ${chapter.chapter_name}`}
                            >
                              <HolderOutlined />
                            </div>
                          </Tooltip>
                          <div className="chapter-row-main">
                            {editingChapterGuid === chapter.chapter_guid ? (
                              <Input
                                value={editedChapterName}
                                onChange={onEditChapterName}
                                className="chapter-row-edit-input"
                                disabled={isInteractionLocked}
                                onClick={(event) => event.stopPropagation()}
                              />
                            ) : (
                              <span className="chapter-row-title-wrap">
                                {pendingProofreadReviewChapterGuids?.has(chapter.chapter_guid) ? (
                                  <Tooltip
                                    title={`This chapter has pending proofreading changes for ${pendingProofreadReviewLabel}. Open the Proofread view to review them.`}
                                  >
                                    <span
                                      aria-label={`Pending proofread review for ${chapter.chapter_name}`}
                                      className="chapter-row-status-icon"
                                      onClick={(event) => event.stopPropagation()}
                                    >
                                      <InfoCircleOutlined />
                                    </span>
                                  </Tooltip>
                                ) : null}
                                <span className="chapter-row-title">
                                  {chapter.chapter_name}
                                </span>
                              </span>
                            )}
                          </div>
                          <div
                            className="chapter-row-actions"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {editingChapterGuid === chapter.chapter_guid ? (
                              <>
                                <Button
                                  type="text"
                                  size="small"
                                  aria-label={`Save chapter name for ${chapter.chapter_name}`}
                                  icon={<CheckOutlined />}
                                  onClick={() => {
                                    if (isInteractionLocked) {
                                      return;
                                    }
                                    onSaveChapterName(chapter.chapter_guid);
                                  }}
                                  disabled={isInteractionLocked}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  aria-label={`Cancel renaming ${chapter.chapter_name}`}
                                  icon={<CloseOutlined />}
                                  onClick={() => {
                                    if (isInteractionLocked) {
                                      return;
                                    }
                                    onCancelEdit();
                                  }}
                                  disabled={isInteractionLocked}
                                />
                              </>
                            ) : (
                              <Dropdown
                                trigger={['click']}
                                disabled={isInteractionLocked}
                                menu={{
                                  items: [
                                    {
                                      key: 'rename',
                                      label: (
                                        <span onClick={() => onStartEditingChapter(chapter)}>
                                          Rename chapter
                                        </span>
                                      ),
                                      icon: <EditOutlined />,
                                    },
                                    ...(allowSourceChapterDeletion
                                      ? [{
                                          key: 'delete',
                                          label: (
                                            <span onClick={() => onDeleteChapter(chapter.chapter_guid)}>
                                              Delete source chapter
                                            </span>
                                          ),
                                          icon: <DeleteOutlined />,
                                          danger: true,
                                        }]
                                      : []),
                                  ],
                                }}
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  className="chapter-row-menu-button"
                                  icon={<MoreOutlined />}
                                  aria-label={`Chapter actions for ${chapter.chapter_name}`}
                                />
                              </Dropdown>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        
        {isSidebarCollapsed && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            marginTop: '50px' 
          }}>
            <Tooltip title="Chapters" placement="right">
              <div className="chapter-sidebar-collapsed-label">
                <div>
                  CHAPTER LIST
                </div>
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChapterList;
